/* eslint-disable react/prop-types */
import React, { memo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { Box, IconButton, Typography } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ReactJson from 'react-json-view';
import { downloadJson } from '../utils/downloadUtils'; // Import the utility

// Define the expected structure of the data prop for this node type
interface ProcessStepNodeData {
  label: string; // e.g., "Step 1: fraktal"
  processData: any; // The actual JSON object for this step
}

const ProcessStepNode: React.FC<NodeProps<ProcessStepNodeData>> = memo(({ data }) => {
  const handleDownload = () => {
    // Extract step number from label for filename
    const stepNumber = data.label.split(':')[0].replace(/\s+/g, '_').toLowerCase();
    const filename = `${stepNumber}_data`; // Filename base
    // Use the utility function
    downloadJson(data.processData, filename);
  };

  return (
    <Box
      sx={{
        padding: 2,
        borderRadius: 1,
        backgroundColor: 'white',
        border: '1px solid #ccc',
        maxWidth: '800px',
        position: 'relative',
      }}
    >
      {/* Node Handle (Top - Target) */}
      <Handle
        type="target"
        position={Position.Top}
        style={{ background: '#555' }}
        isConnectable={true}
      />

      {/* Node Content */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
          {data.label}
        </Typography>
        <IconButton
          onClick={handleDownload}
          size="small"
          sx={{
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          <DownloadIcon />
        </IconButton>
      </Box>
      <Box sx={{ maxHeight: '300px', overflow: 'auto' }}>
        <ReactJson
          src={data.processData}
          theme="rjv-default"
          name={false}
          collapsed={1}
          collapseStringsAfterLength={80}
          displayDataTypes={false}
          displayObjectSize={false}
          enableClipboard={false}
          style={{
            fontSize: '0.75rem',
            backgroundColor: '#f5f5f5',
            borderRadius: '4px',
            padding: '8px',
          }}
        />
      </Box>

      {/* Node Handle (Bottom - Source) */}
      <Handle
        type="source"
        position={Position.Bottom}
        style={{ background: '#555' }}
        isConnectable={true}
      />
    </Box>
  );
});

ProcessStepNode.displayName = 'ProcessStepNode';

export default ProcessStepNode;
